/**
 * Connector instance detail page component.
 * View and edit connector instance configuration.
 */

// React Imports
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Chip,
  Tabs,
  Tab,
  Alert,
  CircularProgress,
  IconButton,
  Tooltip,
  Card,
  CardContent,
  Skeleton,
  FormControlLabel,
  Switch,
  Grid,
  Divider,
  TextField,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Delete as DeleteIcon,
  ContentCopy as DuplicateIcon,
  PlayArrow as TestIcon,
} from '@mui/icons-material';
import { connectorService } from '../services/connectorService';
import { useNotificationStore } from '../store/notificationStore';
import { ConnectorIcon } from '../components/connectors/ConnectorIcon';
import { ConnectionStatusIndicator } from '../components/connectors/ConnectionStatusIndicator';
import { OAuthConnectButton } from '../components/connectors/OAuthConnectButton';
import { ConnectorTestPanel } from '../components/connectors/ConnectorTestPanel';
import { SettingsForm } from '../components/connectors/SettingsForm';
import { ConnectorConfigRenderer } from '../components/connectors/ConnectorConfigRenderer';
import { CapabilityList } from '../components/connectors/CapabilityList';
import { RateLimitStatusCard } from '../components/connectors/RateLimitStatusCard';
import type {
  ConnectorInstanceDetailDto,
  UpdateConnectorInstanceRequest,
  RateLimitStatus,
  ConnectorUsageStats,
} from '../types/connector';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel = ({ children, value, index }: TabPanelProps) => (
  <div role="tabpanel" hidden={value !== index}>
    {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
  </div>
);

/**
 * Connector instance detail page component.
 */
export const ConnectorInstanceDetailPage = () => {
  const { instanceId } = useParams<{ instanceId: string }>();
  const navigate = useNavigate();
  const { addNotification } = useNotificationStore();

  const [instance, setInstance] = useState<ConnectorInstanceDetailDto | null>(null);
  const [rateLimitStatus, setRateLimitStatus] = useState<RateLimitStatus | null>(null);
  const [usageStats, setUsageStats] = useState<ConnectorUsageStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tabValue, setTabValue] = useState(0);

  // Form data
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [rateLimitPerMinute, setRateLimitPerMinute] = useState<number | ''>('');
  const [rateLimitPerHour, setRateLimitPerHour] = useState<number | ''>('');
  const [rateLimitPerDay, setRateLimitPerDay] = useState<number | ''>('');
  const [settingsJson, setSettingsJson] = useState('{}');
  const [enabledCapabilityIds, setEnabledCapabilityIds] = useState<string[]>([]);

  useEffect(() => {
    if (instanceId) {
      loadInstance();
      loadRateLimitStatus();
      loadUsageStats();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [instanceId]);

  const loadInstance = async () => {
    if (!instanceId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await connectorService.getConnectorInstance(instanceId);
      setInstance(data);
      setName(data.name);
      setDescription(data.description || '');
      setIsActive(data.isActive);
      setRateLimitPerMinute(data.rateLimitPerMinute || '');
      setRateLimitPerHour(data.rateLimitPerHour || '');
      setRateLimitPerDay(data.rateLimitPerDay || '');
      
      // Load settings
      try {
        const settings = await connectorService.getConnectorSettings(instanceId);
        setSettingsJson(settings.settingsJson || '{}');
      } catch {
        // Settings might not exist yet
      }
      
      // Get enabled capabilities from connector
      if (data.connector) {
        setEnabledCapabilityIds(data.connector.capabilities.filter((c) => c.isActive).map((c) => c.id));
      }
    } catch (err: any) {
      const msg = err.response?.data?.message || 'Failed to load connector instance';
      setError(msg);
      addNotification(msg, 'error');
    } finally {
      setLoading(false);
    }
  };

  const loadRateLimitStatus = async () => {
    if (!instanceId) return;
    try {
      const status = await connectorService.getRateLimitStatus(instanceId);
      setRateLimitStatus(status);
    } catch {
      // Rate limit status might not be available
    }
  };

  const loadUsageStats = async () => {
    if (!instanceId) return;
    try {
      const stats = await connectorService.getConnectorUsageStats(instanceId);
      setUsageStats(stats);
    } catch {
      // Usage stats might not be available
    }
  };

  const handleSave = async () => {
    if (!instanceId) return;
    setSaving(true);
    setError(null);
    try {
      const request: UpdateConnectorInstanceRequest = {
        name: name.trim(),
        description: description.trim() || undefined,
        isActive,
        rateLimitPerMinute: rateLimitPerMinute ? Number(rateLimitPerMinute) : undefined,
        rateLimitPerHour: rateLimitPerHour ? Number(rateLimitPerHour) : undefined,
        rateLimitPerDay: rateLimitPerDay ? Number(rateLimitPerDay) : undefined,
        settingsJson: settingsJson || '{}',
      };

      await connectorService.updateConnectorInstance(instanceId, request);
      addNotification('Connector instance updated successfully', 'success');
      await loadInstance();
      await loadRateLimitStatus();
    } catch (err: any) {
      const msg = err.response?.data?.message || 'Failed to update connector instance';
      setError(msg);
      addNotification(msg, 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!instanceId) return;
    if (!window.confirm(`Are you sure you want to delete "${name}"?`)) return;
    try {
      await connectorService.deleteConnectorInstance(instanceId);
      addNotification('Connector instance deleted successfully', 'success');
      navigate('/connectors');
    } catch (err: any) {
      addNotification(err.response?.data?.message || 'Failed to delete connector instance', 'error');
    }
  };

  const handleDuplicate = () => {
    if (instance?.connectorId) {
      navigate(`/connectors/${instance.connectorId}/instances/new`);
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: 6 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 6 }}>
          <CircularProgress />
        </Box>
        <Card>
          <CardContent>
            <Skeleton variant="text" width="60%" height={40} />
            <Skeleton variant="text" width="40%" height={24} sx={{ mt: 2 }} />
            <Skeleton variant="rectangular" height={200} sx={{ mt: 3 }} />
          </CardContent>
        </Card>
      </Box>
    );
  }

  if (!instance) {
    return (
      <Box sx={{ p: 6 }}>
        <Alert severity="error">{error || 'Connector instance not found'}</Alert>
      </Box>
    );
  }

  const connectionStatus: 'Connected' | 'Disconnected' | 'Expired' | 'Error' | 'Unknown' =
    instance.isCredentialValid === true
      ? 'Connected'
      : instance.isCredentialValid === false
      ? 'Disconnected'
      : 'Unknown';

  return (
    <Box sx={{ p: 6 }}>
      {/* Page Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 6 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexGrow: 1 }}>
          <Button startIcon={<BackIcon />} onClick={() => navigate('/connectors')} variant="outlined">
            Back
          </Button>
          {instance.connector && (
            <ConnectorIcon
              connectorKey={instance.connector.key}
              category={instance.connector.category}
              sx={{ fontSize: 40, color: 'primary.main' }}
            />
          )}
          <Box>
            <Typography variant="h4" component="h1" fontWeight={600}>
              {instance.name}
            </Typography>
            {instance.connector && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {instance.connector.name} Instance
              </Typography>
            )}
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 2, alignItems: 'center' }}>
              <ConnectionStatusIndicator status={connectionStatus} />
              <Chip
                label={instance.isActive ? 'Active' : 'Inactive'}
                size="small"
                color={instance.isActive ? 'success' : 'default'}
                variant="outlined"
              />
            </Box>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Test Connection">
            <IconButton onClick={() => setTabValue(1)}>
              <TestIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Duplicate">
            <IconButton onClick={handleDuplicate}>
              <DuplicateIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton color="error" onClick={handleDelete}>
              <DeleteIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 6 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Tabs */}
      <Card>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={tabValue} onChange={(_, newValue) => setTabValue(newValue)}>
            <Tab label="Overview" />
            <Tab label="Configuration" />
            <Tab label="Settings" />
            <Tab label="Capabilities" />
            <Tab label="Rate Limits" />
            <Tab label="Usage & Analytics" />
            {instance.webhooks && instance.webhooks.length > 0 && <Tab label="Webhooks" />}
          </Tabs>
        </Box>

        <CardContent>
          {/* Overview Tab */}
          <TabPanel value={tabValue} index={0}>
            <Grid spacing={3}>
              <Grid size={{ xs: 12, md: 8 }}>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Instance Information
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Name
                    </Typography>
                    <Typography variant="body1" fontWeight={500}>
                      {instance.name}
                    </Typography>
                  </Box>
                  {instance.description && (
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Description
                      </Typography>
                      <Typography variant="body1">{instance.description}</Typography>
                    </Box>
                  )}
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Connection Status
                    </Typography>
                    <Box sx={{ mt: 1 }}>
                      <ConnectionStatusIndicator status={connectionStatus} />
                    </Box>
                  </Box>
                </Box>
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="h6" sx={{ mb: 2 }}>
                      Quick Stats
                    </Typography>
                    {usageStats && (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            Total Calls
                          </Typography>
                          <Typography variant="h5" fontWeight={600}>
                            {usageStats.totalCalls.toLocaleString()}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            Success Rate
                          </Typography>
                          <Typography variant="h5" fontWeight={600}>
                            {usageStats.totalCalls > 0
                              ? ((usageStats.successfulCalls / usageStats.totalCalls) * 100).toFixed(1)
                              : 0}
                            %
                          </Typography>
                        </Box>
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            Avg Duration
                          </Typography>
                          <Typography variant="h5" fontWeight={600}>
                            {usageStats.averageDurationMs.toFixed(0)}ms
                          </Typography>
                        </Box>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </TabPanel>

          {/* Configuration Tab */}
          <TabPanel value={tabValue} index={1}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <Typography variant="h6" fontWeight={600}>
                Basic Information
              </Typography>
              <TextField
                label="Instance Name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                fullWidth
              />
              <TextField
                label="Description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                multiline
                rows={3}
                fullWidth
              />
              <FormControlLabel
                control={<Switch checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />}
                label="Active"
              />

              <Divider sx={{ my: 2 }} />

              <Typography variant="h6" fontWeight={600}>
                Authentication
              </Typography>
              {instance.authenticationType === 'OAuth2' && (
                <OAuthConnectButton
                  instanceId={instanceId!}
                  connectorName={instance.connector?.name || 'Connector'}
                  isConnected={connectionStatus === 'Connected'}
                  onConnected={() => {
                    loadInstance();
                    addNotification('Successfully connected', 'success');
                  }}
                  onError={(err) => setError(err)}
                />
              )}
              {instance.authenticationType !== 'OAuth2' && (
                <Alert severity="info">
                  Authentication Type: <strong>{instance.authenticationType}</strong>
                  <br />
                  Credentials are securely stored and managed by the system.
                </Alert>
              )}

              <ConnectorTestPanel
                instanceId={instanceId!}
                connectorName={instance.connector?.name || 'Connector'}
              />

              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
                <Button variant="contained" onClick={handleSave} disabled={saving}>
                  {saving ? 'Saving...' : 'Save Changes'}
                </Button>
              </Box>
            </Box>
          </TabPanel>

          {/* Settings Tab */}
          <TabPanel value={tabValue} index={2}>
            {/* Connector-specific configuration (renders specialized config or generic form) */}
            {instance.connector ? (
              <ConnectorConfigRenderer
                connector={instance.connector}
                instanceId={instanceId}
                settingsJson={settingsJson}
                isConnected={connectionStatus === 'Connected'}
                level="Instance"
                onChange={setSettingsJson}
                onConnected={() => {
                  loadInstance();
                  addNotification('Settings updated successfully', 'success');
                }}
              />
            ) : (
              <SettingsForm
                settingsJson={settingsJson}
                level="Instance"
                onChange={setSettingsJson}
              />
            )}
            
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
              <Button variant="contained" onClick={handleSave} disabled={saving}>
                {saving ? 'Saving...' : 'Save Settings'}
              </Button>
            </Box>
          </TabPanel>

          {/* Capabilities Tab */}
          <TabPanel value={tabValue} index={3}>
            {instance.connector && (
              <CapabilityList
                capabilities={instance.connector.capabilities}
                enabledCapabilityIds={enabledCapabilityIds}
                onToggle={(capabilityId, enabled) => {
                  if (enabled) {
                    setEnabledCapabilityIds([...enabledCapabilityIds, capabilityId]);
                  } else {
                    setEnabledCapabilityIds(enabledCapabilityIds.filter((id) => id !== capabilityId));
                  }
                }}
                showDetails
              />
            )}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
              <Button variant="contained" onClick={handleSave} disabled={saving}>
                {saving ? 'Saving...' : 'Save Capabilities'}
              </Button>
            </Box>
          </TabPanel>

          {/* Rate Limits Tab */}
          <TabPanel value={tabValue} index={4}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {rateLimitStatus && <RateLimitStatusCard status={rateLimitStatus} />}
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6" sx={{ mb: 2 }}>
                    Rate Limit Configuration
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <TextField
                      label="Per Minute"
                      type="number"
                      value={rateLimitPerMinute}
                      onChange={(e) => setRateLimitPerMinute(e.target.value ? Number(e.target.value) : '')}
                      helperText="Leave empty to inherit from connector defaults"
                    />
                    <TextField
                      label="Per Hour"
                      type="number"
                      value={rateLimitPerHour}
                      onChange={(e) => setRateLimitPerHour(e.target.value ? Number(e.target.value) : '')}
                      helperText="Leave empty to inherit from connector defaults"
                    />
                    <TextField
                      label="Per Day"
                      type="number"
                      value={rateLimitPerDay}
                      onChange={(e) => setRateLimitPerDay(e.target.value ? Number(e.target.value) : '')}
                      helperText="Leave empty to inherit from connector defaults"
                    />
                  </Box>
                </CardContent>
              </Card>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button variant="contained" onClick={handleSave} disabled={saving}>
                  {saving ? 'Saving...' : 'Save Rate Limits'}
                </Button>
              </Box>
            </Box>
          </TabPanel>

          {/* Usage & Analytics Tab */}
          <TabPanel value={tabValue} index={5}>
            {usageStats ? (
              <Grid spacing={3}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" sx={{ mb: 2 }}>
                        Usage Statistics
                      </Typography>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            Total Calls
                          </Typography>
                          <Typography variant="h4" fontWeight={600}>
                            {usageStats.totalCalls.toLocaleString()}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            Successful Calls
                          </Typography>
                          <Typography variant="h5" fontWeight={600} color="success.main">
                            {usageStats.successfulCalls.toLocaleString()}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            Failed Calls
                          </Typography>
                          <Typography variant="h5" fontWeight={600} color="error.main">
                            {usageStats.failedCalls.toLocaleString()}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            Average Duration
                          </Typography>
                          <Typography variant="h5" fontWeight={600}>
                            {usageStats.averageDurationMs.toFixed(0)}ms
                          </Typography>
                        </Box>
                        {usageStats.totalCost > 0 && (
                          <Box>
                            <Typography variant="caption" color="text.secondary">
                              Total Cost
                            </Typography>
                            <Typography variant="h5" fontWeight={600}>
                              ${usageStats.totalCost.toFixed(2)}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" sx={{ mb: 2 }}>
                        Success Rate
                      </Typography>
                      <Box sx={{ textAlign: 'center', py: 4 }}>
                        <Typography variant="h2" fontWeight={700} color="success.main">
                          {usageStats.totalCalls > 0
                            ? ((usageStats.successfulCalls / usageStats.totalCalls) * 100).toFixed(1)
                            : 0}
                          %
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            ) : (
              <Alert severity="info">No usage data available yet.</Alert>
            )}
          </TabPanel>

          {/* Webhooks Tab */}
          {instance.webhooks && instance.webhooks.length > 0 && (
            <TabPanel value={tabValue} index={6}>
              <Alert severity="info">Webhook management coming soon.</Alert>
            </TabPanel>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};
